import React, { CSSProperties } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { IAnswer } from "../../../models/Quiz";
import PiBasicQuizCheckboxV2 from "../components/v2/PiQuizCheckboxV2";
import PiBasicQuizAnswerTextfieldV2 from "../components/v2/PiBasicQuizAnswerTextfieldV2";
import { TailGeometry } from "./dialogQuizTails";
import PiTooltip from "../../menu/PiTooltip";

/**
 * One answer, drawn like the in-game dialog bubble: the tail and the correct
 * check sit on the side facing the actor, delete shows on hover.
 */
const DialogQuizAnswerBubble = React.forwardRef<
  HTMLDivElement,
  {
    answer: IAnswer;
    actorOnLeft: boolean;
    tail?: TailGeometry;
    canDelete: boolean;
    onAnswerChange: (answer: IAnswer) => void;
    onDelete: () => void;
  }
>(function DialogQuizAnswerBubble(props, ref) {
  const { answer, actorOnLeft, tail, canDelete, onAnswerChange, onDelete } = props;
  const { t } = useTranslation();

  const toggleCorrect = () => onAnswerChange({ ...answer, correct: !answer.correct });

  const tailStyle = (tail ? { "--tail-y": `${tail.y}px`, "--tail-dy": `${tail.dy}px` } : {}) as CSSProperties;

  return (
    <div
      ref={ref}
      className={classNames("dialogquiz-bubble group", {
        "dialogquiz-bubble--tail-left": actorOnLeft,
        "dialogquiz-bubble--tail-right": !actorOnLeft,
      })}
      style={tailStyle}
      data-testid="dialog-quiz-answer"
    >
      {/* Delete sits inside the bubble on the side away from the actor, which the
          actor-hugging text leaves empty. With the minimum left it stays, dimmed,
          and its tooltip says why (aria-disabled, so the tooltip still shows). */}
      <PiTooltip
        tooltipLocId={canDelete ? "dialog_quiz.delete_answer" : "dialog_quiz.min_answers"}
        anchor={
          <button
            type="button"
            className="dialogquiz-delete"
            aria-label={String(t(canDelete ? "dialog_quiz.delete_answer" : "dialog_quiz.min_answers"))}
            aria-disabled={!canDelete}
            onClick={() => canDelete && onDelete()}
          >
            <DeleteOutlineIcon sx={{ fontSize: 20 }} />
          </button>
        }
      />
      <div className="min-w-0 flex-1">
        {/* Text hugs the side facing the actor, like the game. */}
        <PiBasicQuizAnswerTextfieldV2 localizationKey={answer.text} wrap alignRight={!actorOnLeft} />
      </div>
      {/* The wrapper owns the toggle so it can be a real, keyboard-operable checkbox. */}
      <div
        className="dialogquiz-check"
        role="checkbox"
        aria-checked={answer.correct}
        aria-label={String(t("dialog_quiz.mark_correct"))}
        title={String(t("dialog_quiz.mark_correct"))}
        tabIndex={0}
        onClick={toggleCorrect}
        onKeyDown={(event) => {
          if (event.key === " " || event.key === "Enter") {
            event.preventDefault();
            toggleCorrect();
          }
        }}
      >
        <PiBasicQuizCheckboxV2 checked={answer.correct} onChange={() => undefined} />
      </div>
    </div>
  );
});

export default DialogQuizAnswerBubble;
